import { useTranslation } from 'react-i18next'
import { useCallSelector } from '../selectors'
import { useTick } from '../ticker'

/**
 * CHRONOMÈTRE de l'appel — et RIEN d'autre.
 *
 * C'est le composant qui se redessine une fois par seconde. Il est isolé pour
 * cette seule raison : la contrainte de perf du cahier veut que « le tic ne
 * re-rende QUE son compteur, jamais la scène ». Avant, la minuterie vivait
 * dans `ActiveCallPanel`, qui porte aussi le micro, le clavier DTMF et le
 * bouton raccrocher — tout se redessinait chaque seconde.
 *
 * L'instant de départ (`startedAt`) vient de la machine : il est posé au
 * DÉCROCHÉ RÉEL, jamais au lancement. Un appel pré-lancé ne compte donc pas
 * les secondes de sa sonnerie, et le chronomètre survit à une navigation.
 *
 * ⚠️ CHIFFRES TABULAIRES, toujours. Sans eux, le « 1 » étant plus étroit que
 * les autres chiffres, tout le compteur se décale à chaque seconde — sur un
 * nombre qui change soixante fois par minute, c'est un tremblement permanent
 * dans le coin de l'œil de l'agent.
 */
export interface CallTimerProps {
  /** `hero` : 32 px, le chiffre principal du panneau d'appel (maquette). */
  size?: 'inline' | 'hero'
}

export function CallTimer({ size = 'inline' }: CallTimerProps = {}) {
  const { t } = useTranslation('telephony')
  const startedAt = useCallSelector((state) => state.startedAt)
  useTick(startedAt !== null)

  const seconds = startedAt
    ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
    : 0
  const minutes = Math.floor(seconds / 60)

  return (
    <span
      aria-label={t('call.durationLabel')}
      className={
        size === 'hero'
          ? 'block font-mono text-[32px] font-semibold leading-none tabular-nums text-gray-900 dark:text-white'
          : 'font-mono text-2xl tabular-nums text-gray-900 dark:text-white'
      }
    >
      {String(minutes).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
    </span>
  )
}
